import { useCallback, useState } from "react";

import { faDiagramNested } from "@fortawesome/pro-regular-svg-icons";
import { m } from "framer-motion";

import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatEmptyState } from "src/base-components/Chat/ChatEmptyState";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { Pill } from "src/base-components/Pill";
import { ResizeHandleButton } from "src/base-components/ResizableSidepane";
import { useResizableWidth } from "src/base-components/useResizableWidth";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { PromptBox } from "src/copilot/PromptBox";
import { CopilotSidebarHeader } from "src/flow/analytics/CopilotSidebarHeader";
import { LiveAnalyticsMessages } from "src/flow/analytics/m1/LiveAnalyticsMessages";
import { useLiveAnalyticsChat } from "src/flow/analytics/m1/useLiveAnalyticsChat";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "Is my approval rate dropping?",
    prompt: "Is my approval rate dropping?",
  },
  {
    label: "How does my approval rate compare to last week?",
    prompt: "How does my approval rate compare to last week?",
  },
  {
    label: "What is the Total Amount Lent by version every day?",
    prompt: "What is the Total Amount Lent by version every day?",
  },
];

const MIN_WIDTH = 420;
const MAX_WIDTH = 800;

export const CopilotSidebar: React.FC<{
  onClose: () => void;
  flowId: string;
  flowName: string;
}> = ({ onClose, flowId, flowName }) => {
  const { width, isResizing, handleResizeStart } = useResizableWidth({
    side: "right",
    storageKey: "copilot-sidebar-width",
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidth: 528,
  });

  const [draft, setDraft] = useState("");
  const { status, errorMessage, messages, send } = useLiveAnalyticsChat({
    flowId,
    flowName,
  });

  const busy = status === "provisioning" || status === "thinking";

  const handleSubmit = useCallback(
    (message: string) => {
      if (!message.trim() || busy) return;
      setDraft("");
      void send(message);
    },
    [busy, send],
  );

  const handleReset = useCallback(() => {
    setDraft("");
  }, []);

  return (
    <FloatingWindowsProvider>
      <m.div
        animate={{ width }}
        className="shrink-0 overflow-hidden"
        exit={{ width: 0 }}
        initial={{ width: 0 }}
        transition={{ duration: isResizing ? 0 : 0.4, ease: "easeInOut" }}
      >
        <aside
          className="relative flex h-full flex-col border-gray-200 border-l bg-white"
          data-loc="copilot-sidebar"
          style={{ width }}
        >
          <ResizeHandleButton onResizeStart={handleResizeStart} />

          <ChatBody>
            <div className="shrink-0 px-4">
              <CopilotSidebarHeader onClose={onClose} />
            </div>

            <div className="flex-1 px-4">
              <ChatMessageList>
                {messages.length === 0 && status !== "error" ? (
                  <ChatEmptyState
                    dataLoc="copilot-empty-state"
                    prompts={SUGGESTION_PROMPTS}
                    variant="new"
                    onPromptClick={handleSubmit}
                  />
                ) : (
                  <LiveAnalyticsMessages
                    errorMessage={errorMessage}
                    messages={messages}
                    status={status}
                  />
                )}
              </ChatMessageList>
            </div>

            <div className="px-4 pb-4">
              <PromptBox
                disabled={busy || draft.trim().length === 0}
                errored={false}
                mentionTextPlaceholder=""
                minified={false}
                resource={[]}
                resourceType={
                  FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
                }
                topContent={
                  <Pill size="sm" variant="gray">
                    <Pill.Icon icon={faDiagramNested} />
                    <Pill.Text>{flowName}</Pill.Text>
                  </Pill>
                }
                value={draft}
                onChange={setDraft}
                onReset={handleReset}
                onSubmit={(content) => handleSubmit(content)}
              />
            </div>
          </ChatBody>
        </aside>
      </m.div>
    </FloatingWindowsProvider>
  );
};
